import * as userDates from "../../common/dateTimePreferences";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  parseApiDate,
  type ManualScoreMark,
  type ValidationSource,
} from "../../../api/contactValidation";
import { linkifyText } from "../../../utils/linkify";
import { useGridDetailMode } from "../../common/GridDetailMode";

/**
 * The confidence bands the checks are scored against.
 *
 * Six bands rather than a red/amber/green traffic light, because the whole
 * point of the scoring instruction is that the model must use the full range
 * instead of clustering everything at "probably fine". Three colours would
 * flatten 70 and 94 into the same signal and hide exactly the distinction the
 * scores exist to make.
 */
const BANDS = [
  { min: 95, label: "Extremely strong", fg: "#166534", bg: "#dcfce7", border: "#bbf7d0" },
  { min: 85, label: "Strong", fg: "#15803d", bg: "#f0fdf4", border: "#d5f0da" },
  { min: 70, label: "Likely, with a caveat", fg: "#a16207", bg: "#fefce8", border: "#fde68a" },
  { min: 50, label: "Material uncertainty", fg: "#b45309", bg: "#fff7ed", border: "#fed7aa" },
  { min: 25, label: "Probably fails", fg: "#c2410c", bg: "#fff1f2", border: "#fecdd3" },
  { min: 0, label: "Fails", fg: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
];

const bandFor = (score: number) =>
  BANDS.find((band) => score >= band.min) ?? BANDS[BANDS.length - 1];

/**
 * The contact's LinkedIn column as something a browser can open.
 *
 * The column is free text: it arrives with the scheme, without it, or as the
 * "-" the grid shows for an empty cell, and a link built from the last of
 * those would navigate to a page on this app.
 */
const linkedInProfileUrl = (raw?: string | null): string | null => {
  const value = (raw ?? "").trim();
  if (!value || value === "-" || !/linkedin\.com/i.test(value)) return null;
  return /^https?:\/\//i.test(value) ? value : `https://${value.replace(/^\/+/, "")}`;
};

export interface ValidationCellProps {
  score?: number | null;
  comments?: string | null;
  sources?: ValidationSource[];
  checkedAt?: string | null;
  /** Short prefix inside the chip, e.g. "Fit" or "Data". Omit for score only. */
  label?: string;
  /**
   * The score the check itself returned, when `score` is the 100 a manual
   * verification raised it to.
   *
   * The popover says so rather than quietly swapping the number: the comments
   * underneath still describe what the model found, and a chip reading 100
   * above a paragraph about a missing job title is only confusing without the
   * line explaining that a person overruled it.
   */
  overriddenFrom?: number | null;
  /**
   * Set when this score was typed in by a person rather than produced by a
   * run — from the bulk update panel, from the action panel beside the cell,
   * or from "Mark as verified" on the contact.
   *
   * A hand-set 100 and an earned 100 are the same number in the same column,
   * and the comments underneath still describe whatever the last run found —
   * so a chip reading 100 above a paragraph about a missing job title is only
   * confusing without the line saying a person overruled it. The note is
   * shown in the popover rather than on the chip: the grid is scanned on the
   * score, and a second badge in every cell would cost more than it says.
   */
  manualMark?: ManualScoreMark | null;
  /**
   * Adds the spec's "Check LinkedIn" prompt below the comments whenever the
   * score is short of certain. Only the live contact check sets this — it is
   * the one whose answer a person can go and confirm in one click.
   */
  showLinkedInHint?: boolean;
  /**
   * The contact's LinkedIn profile, which turns the "Check LinkedIn" prompt
   * into the click it is asking for. Without one the prompt stays as text —
   * the profile tab knows the scores but not the contact's fields.
   */
  linkedInUrl?: string | null;
  /**
   * Marks the chip when the data integrity check has offered a correction
   * nobody has acted on yet.
   *
   * Only a marker. The corrections themselves are listed under the score in
   * the Data integrity column, where there is room to show what each one would
   * replace — this is what makes a fixable row findable by eye in the combined
   * Checks cell, which has no room for any of that.
   */
  hasPendingSuggestion?: boolean;
}

/**
 * One score in the contact grid: a coloured confidence badge that opens the
 * comments and the evidence behind it.
 *
 * The comments are behind a click rather than in the cell because they run to
 * a paragraph, and a grid of paragraphs is unreadable — but they are the part
 * that tells a user *why*, so they are one click away and never truncated in
 * the popover.
 */
const ValidationCell: React.FC<ValidationCellProps> = ({
  score,
  comments,
  sources = [],
  checkedAt,
  label,
  overriddenFrom,
  manualMark,
  showLinkedInHint = false,
  linkedInUrl,
  hasPendingSuggestion = false,
}) => {
  /**
   * The grid's "Details" setting. In a detail mode the comments, the evidence
   * and the override note are written into the column underneath the chip
   * instead of waiting behind a hover — which is what makes a column of
   * scores readable when the user is working through them rather than
   * scanning past them. Whether this particular cell is one of the expanded
   * ones is settled below, once its score is known.
   */
  const detailMode = useGridDetailMode();
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] =
    useState<{ top?: number; bottom?: number; left: number; maxHeight: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  /**
   * The popover itself, so a click inside it is not read as a click outside.
   * Without this the source links are unusable: the popover is portalled to
   * the body, so pressing a link closed it on mousedown and the click landed
   * on nothing.
   */
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * A click pins the popover open so it survives the pointer leaving. Without
   * this, hover-to-open would make the source links unreachable — they would
   * vanish the moment you moved towards them.
   */
  const isPinned = useRef(false);

  const cancelHoverTimer = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  useEffect(() => cancelHoverTimer, []);

  // Close on any outside click or Escape. The popover is portalled to the body
  // so it can escape the table's overflow clipping, which means it cannot rely
  // on a parent's blur.
  useEffect(() => {
    if (!isOpen) return;

    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === "Escape") {
          isPinned.current = false;
          setIsOpen(false);
        }
        return;
      }

      const target = event.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        isPinned.current = false;
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);

    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [isOpen]);

  if (score === null || score === undefined) {
    return <span style={{ color: "#9ca3af" }}>—</span>;
  }

  const band = bandFor(score);
  const profileUrl = linkedInProfileUrl(linkedInUrl);
  const isOverridden = typeof overriddenFrom === "number";
  const isManual = !!manualMark;
  const hasDetail =
    !!comments?.trim() ||
    sources.length > 0 ||
    showLinkedInHint ||
    isOverridden ||
    isManual;

  /**
   * Whether this cell is one the chosen detail mode opens. The others fall
   * back to the collapsed chip, detail and all still a hover away — so a
   * filter narrows what is written out, it never hides anything.
   */
  const expanded =
    detailMode === "all" ||
    (detailMode === "updated" && (isManual || isOverridden)) ||
    (detailMode === "incomplete" && score < 100);

  const positionAndOpen = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      // Open upwards when there is no room below, so a score near the bottom of
      // a long list does not open off-screen.
      //
      // Upwards pins `bottom` rather than a `top` computed from the maximum
      // height: the popover is as tall as its comments, so subtracting the
      // maximum left a short one floating well above the chip it belongs to.
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;

      setAnchor({
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 6 }
          : { top: rect.bottom + 6 }),
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 380)),
        maxHeight: Math.min(280, Math.max(140, openUp ? spaceAbove : spaceBelow)),
      });
    }
    setIsOpen(true);
  };

  // Opening on hover is what makes a grid of scores readable — you sweep the
  // column instead of clicking every cell. The short delay stops the popover
  // flickering open as the pointer crosses chips on its way somewhere else.
  const handleMouseEnter = () => {
    // Expanded, the detail is already on screen; opening it again over the top
    // of itself only covers the rows underneath.
    if (!hasDetail || expanded) return;
    cancelHoverTimer();
    hoverTimer.current = setTimeout(positionAndOpen, 120);
  };

  const handleMouseLeave = () => {
    cancelHoverTimer();
    if (isPinned.current) return;
    // Long enough to travel from the chip into the popover, which cancels it.
    hoverTimer.current = setTimeout(() => setIsOpen(false), 220);
  };

  const handleClick = () => {
    if (!hasDetail || expanded) return;
    cancelHoverTimer();

    if (isOpen && isPinned.current) {
      isPinned.current = false;
      setIsOpen(false);
      return;
    }

    isPinned.current = true;
    positionAndOpen();
  };

  /**
   * Everything behind the score: the override note, the comments, the
   * LinkedIn prompt and the sources.
   *
   * One definition for both homes. The popover and the expanded column are
   * the same evidence in two places, and two copies of it would drift apart
   * the first time one of them gained a line.
   */
  const detailBody = (
    <>
      {isManual && (
        <p
          style={{
            margin: "0 0 8px",
            padding: "6px 8px",
            borderRadius: 8,
            background: "#f1f8f2",
            border: "1px solid #d5f0da",
            color: "#2d7a30",
            fontSize: 12.5,
          }}
        >
          <strong style={{ display: "block", marginBottom: 2 }}>
            Manually checked and marked as verified
          </strong>
          {/* Who did it is stored against the mark, but not shown here: the
              line is a warning about re-running the check, and a name in the
              middle of it is one more thing to read past. */}
          Checked by hand
          {manualMark?.at
            ? ` on ${userDates.formatUserDate(parseApiDate(manualMark.at))}`
            : ""}
          . Do not re-run the check unless you wish to overwrite this manual
          check override.
        </p>
      )}

      {isOverridden && (
        <p
          style={{
            margin: "0 0 8px",
            padding: "6px 8px",
            borderRadius: 8,
            background: "#f1f8f2",
            border: "1px solid #d5f0da",
            color: "#2d7a30",
            fontSize: 12.5,
          }}
        >
          Marked verified by hand. This check itself scored {overriddenFrom}.
        </p>
      )}

      {comments?.trim() ? (
        <>
          {/* Whose verdict this is. Once a person has overruled the score, the
              paragraph underneath is the losing argument, not the current
              state of the contact — without the line saying so, a 100 sits
              directly above a list of everything wrong with the record and
              reads as if the two were describing the same conclusion. */}
          {(isManual || isOverridden) && (
            <p
              style={{
                margin: "0 0 4px",
                fontWeight: 600,
                color: "#6b7280",
              }}
            >
              Previous check which has now been checked and overruled:
            </p>
          )}

          {/* The model writes its evidence into the sentence, so any URL in
              the comments is made clickable rather than left as dead text. */}
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {linkifyText(comments.trim())}
          </p>
        </>
      ) : (
        <p style={{ margin: 0, color: "#6b7280" }}>
          {/* A hand-set score with no date behind it is not a clean run — the
              check has never been run on this contact at all, and saying
              "no issues were reported" would credit it with one. */}
          {isManual && !checkedAt
            ? "This check has not been run on this contact."
            : "No issues were reported."}
        </p>
      )}

      {showLinkedInHint && (
        <p
          style={{ margin: "10px 0 0", fontWeight: 600, color: "#b45309" }}
          title="Opens this contact's LinkedIn profile. If you have downloaded the PitchKraft browser extension then it will automatically check the contact against the data held in LinkedIn."
        >
          {profileUrl ? (
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              style={{ color: "#b45309", textDecoration: "underline" }}
            >
              Check LinkedIn ↗
            </a>
          ) : (
            "Check LinkedIn"
          )}
        </p>
      )}

      {sources.length > 0 && (
        <div style={{ marginTop: 12, borderTop: "1px solid #f0f1f4", paddingTop: 10 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "#6b7280",
              marginBottom: 6,
            }}
          >
            Sources
          </div>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {sources.map((source) => (
              <li key={source.url} style={{ marginBottom: 4 }}>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#2563eb", wordBreak: "break-word" }}
                >
                  {source.label || source.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  const chip = (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        title={hasDetail ? undefined : band.label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 9px",
          whiteSpace: "nowrap",
          borderRadius: 999,
          border: `1px solid ${band.border}`,
          background: band.bg,
          color: band.fg,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: hasDetail ? "pointer" : "default",
          lineHeight: 1.4,
        }}
      >
        {label ? `${label} ${score}` : score}
        {hasPendingSuggestion && (
          // A correction nobody hovers over is a correction nobody applies.
          // The dot is what makes a fixable row findable by eye down a column
          // of scores that otherwise all look the same.
          <span
            title="A correction is waiting to be accepted"
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: band.fg,
            }}
          />
        )}
      </button>
    </>
  );

  if (expanded) {
    return (
      <div style={{ display: "block", textAlign: "left" }}>
        {chip}

        {hasDetail && (
          <div
            style={{
              marginTop: 6,
              padding: "8px 10px",
              borderRadius: 9,
              border: "1px solid #eef0f3",
              background: "#fbfcfd",
              fontSize: 12.5,
              lineHeight: 1.5,
              color: "#0b1220",
              whiteSpace: "normal",
            }}
          >
            {checkedAt && (
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
                {userDates.formatUserDate(parseApiDate(checkedAt))}
              </div>
            )}
            {detailBody}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {chip}

      {isOpen && anchor &&
        createPortal(
          <div
            ref={popoverRef}
            onMouseEnter={cancelHoverTimer}
            onMouseLeave={handleMouseLeave}
            style={{
              position: "fixed",
              top: anchor.top,
              bottom: anchor.bottom,
              left: anchor.left,
              width: 360,
              maxHeight: anchor.maxHeight,
              overflowY: "auto",
              background: "#fff",
              border: "1px solid #e8eaee",
              borderRadius: 12,
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
              padding: 14,
              zIndex: 1100,
              fontSize: 13,
              lineHeight: 1.55,
              color: "#0b1220",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 600, color: band.fg }}>
                {score} · {band.label}
              </span>
              {checkedAt && (
                <span style={{ fontSize: 11.5, color: "#6b7280" }}>
                  {userDates.formatUserDate(parseApiDate(checkedAt))}
                </span>
              )}
            </div>

            {detailBody}
          </div>,
          document.body
        )}
    </>
  );
};

export default ValidationCell;

/**
 * Reads the sources blob the list endpoints send down as a JSON string.
 * Malformed JSON yields no sources rather than taking the cell down with it.
 *
 * Both casings are accepted. The results endpoint returns the list through
 * MVC, which camel-cases it, while the list endpoints hand over the column as
 * it was stored — and that was written with Newtonsoft's default Pascal case.
 * Reading either is what makes the rows already in the database render their
 * links instead of a row of blanks.
 */
export const parseSources = (raw: unknown): ValidationSource[] => {
  const normalise = (list: any[]): ValidationSource[] =>
    list
      .map((item) => ({
        label: item?.label ?? item?.Label ?? "",
        url: item?.url ?? item?.Url ?? "",
      }))
      .filter((source) => !!source.url);

  if (Array.isArray(raw)) return normalise(raw);
  if (typeof raw !== "string" || !raw.trim()) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? normalise(parsed) : [];
  } catch {
    return [];
  }
};
